import React from 'react';
import { Bookmark, BookmarkCheck, ExternalLink, Clock, User, Bot, AlertTriangle, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

export default function ChatWindow({ messages, streamingMessage, isStreaming, onToggleBookmark, onRetry }) {
  const formatMessageTime = (timestamp) => {
    return format(new Date(timestamp), 'MMM d, HH:mm');
  };

  const renderMessage = (message) => {
    const isUser = message?.role === 'user';
    const isSystem = message?.role === 'system';
    const isError = message?.role === 'error';

    if (isError) {
      return (
        <div key={message?.id} className="flex justify-start mb-4">
          <div className="max-w-[80%] bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <span className="text-xs font-medium text-red-700">AI Response Failed</span>
              <span className="text-xs text-red-400">{formatMessageTime(message?.created_at)}</span>
            </div>
            <p className="text-sm text-red-700 mb-3">{message?.content}</p>
            {message?.retryable && message?.originalMessage && onRetry && (
              <button
                onClick={() => onRetry(message?.originalMessage, message?.originalType || 'text')}
                className="flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-800 bg-red-100 hover:bg-red-200 px-3 py-1.5 rounded-md transition-colors"
              >
                <RefreshCw className="w-3 h-3" />
                Retry message
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div
        key={message?.id}
        className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
      >
        <div
          className={`max-w-[80%] rounded-lg px-4 py-3 ${
            isUser
              ? 'bg-green-600 text-white'
              : isSystem
              ? 'bg-blue-50 border border-blue-200 text-blue-800' :'bg-gray-100 text-gray-900'
          }`}
        >
          {/* Message Header */}
          <div className="flex items-center gap-2 mb-2">
            {isUser ? (
              <User className="w-4 h-4" />
            ) : isSystem ? (
              <Clock className="w-4 h-4" />
            ) : (
              <Bot className="w-4 h-4 text-green-600" />
            )}
            <span className="text-xs font-medium">
              {isUser ? 'You' : isSystem ? 'System' : 'GrowGuardian AI'}
            </span>
            <span className="text-xs opacity-60">
              {formatMessageTime(message?.created_at)}
            </span>
          </div>

          {/* Message Content */}
          <div className="space-y-2">
            <div className="whitespace-pre-wrap">{message?.content}</div>

            {/* Referenced Content */}
            {message?.referenced_module && (
              <div className="mt-2 p-2 bg-white/10 rounded border border-white/20">
                <div className="flex items-center gap-2 text-xs">
                  <ExternalLink className="w-3 h-3" />
                  <span>
                    Referenced: Chapter {message?.referenced_module?.chapter_number} - {message?.referenced_module?.title}
                  </span>
                </div>
              </div>
            )}

            {message?.referenced_product && (
              <div className="mt-2 p-2 bg-white/10 rounded border border-white/20">
                <div className="flex items-center gap-2 text-xs">
                  <ExternalLink className="w-3 h-3" />
                  <span>
                    Product: {message?.referenced_product?.name} ({message?.referenced_product?.category})
                  </span>
                  {message?.referenced_product?.use_case && (
                    <span className="text-xs opacity-75">
                      - {message?.referenced_product?.use_case}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Message Metadata */}
            {message?.metadata && message?.metadata?.pest_identified && (
              <div className="mt-2 p-2 bg-white/10 rounded border border-white/20">
                <div className="text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Identified:</span>
                    <span>{message?.metadata?.pest_identified}</span>
                  </div>
                  {message?.metadata?.confidence && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="font-medium">Confidence:</span>
                      <span>{Math.round(message?.metadata?.confidence * 100)}%</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Message Actions */}
          {!isSystem && !isUser && (
            <div className="flex items-center justify-end gap-2 mt-3 pt-2 border-t border-white/20">
              <button
                onClick={() => onToggleBookmark(message?.id)}
                className="flex items-center gap-1 text-xs opacity-60 hover:opacity-100 transition-opacity"
                title={message?.is_bookmarked ? 'Remove bookmark' : 'Bookmark this response'}
              >
                {message?.is_bookmarked ? (
                  <BookmarkCheck className="w-3 h-3" />
                ) : (
                  <Bookmark className="w-3 h-3" />
                )}
                {message?.is_bookmarked ? 'Bookmarked' : 'Bookmark'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStreamingMessage = () => {
    if (!isStreaming || !streamingMessage) return null;

    return (
      <div className="flex justify-start mb-4">
        <div className="max-w-[80%] bg-gray-100 text-gray-900 rounded-lg px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <Bot className="w-4 h-4 text-green-600" />
            <span className="text-xs font-medium">GrowGuardian AI</span>
            <div className="flex space-x-1">
              <div className="w-1 h-1 bg-green-600 rounded-full animate-pulse"></div>
              <div className="w-1 h-1 bg-green-600 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-1 h-1 bg-green-600 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
          <div className="whitespace-pre-wrap">
            {streamingMessage}
            <span className="inline-block w-2 h-4 bg-green-600 animate-pulse ml-1"></span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-2">
      {messages?.map(renderMessage)}
      {renderStreamingMessage()}
      {messages?.length === 0 && !isStreaming && (
        <div className="text-center py-12 text-gray-500">
          <Bot className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium mb-2">Welcome to GrowGuardian AI</h3>
          <p className="text-sm max-w-md mx-auto">
            I'm here to help with pest identification, treatment recommendations, 
            and IPM strategies. Ask me anything about integrated pest management!
          </p>
          <div className="mt-6 space-y-2 text-sm text-gray-400">
            <p>💡 Try asking:</p>
            <div className="flex flex-wrap justify-center gap-2 mt-2">
              <span className="px-3 py-1 bg-gray-100 rounded-full text-xs">
                "I found small white flies on my plants"
              </span>
              <span className="px-3 py-1 bg-gray-100 rounded-full text-xs">
                "What biological controls work for spider mites?"
              </span>
              <span className="px-3 py-1 bg-gray-100 rounded-full text-xs">
                "Help me build an IPM program"
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}